/* eslint-disable react-hooks/exhaustive-deps */
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { CircleX, Wallet2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getUser } from "@/api/users";
import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { getAccountConsent } from "@/api/account";
import { cancelPayment, initPayment, patchPayment } from "@/api/payment";
import { addMinutes, fromUnixTime, getUnixTime } from "date-fns";
import { toast } from "sonner";
import {
  FULL_WIDTH_TALL_BUTTON,
  GREEN_ACTION_BUTTON_DISABLED,
  GREEN_ACTION_BUTTON_ENABLED,
  OUTLINE_PRESSABLE_BUTTON,
} from "@/lib/buttonStyles";

interface Props {
  status: string;
  expiresAt: number;
  paymentId: string;
  isLoading: boolean;
  setRefetchInterval: () => void;
  setIsLoading: (value: boolean) => void;
  openWallet: () => void;
  setAuthPhoneNumber: Dispatch<SetStateAction<string | undefined>>;
  setAuthExternalId: Dispatch<SetStateAction<string>>;
}

export default function InitPaymentCorporateUser({
  status,
  expiresAt,
  paymentId,
  isLoading,
  setRefetchInterval,
  setIsLoading,
  openWallet,
  setAuthPhoneNumber,
  setAuthExternalId,
}: Props) {
  const [fetchUser, setFetchUser] = useState<boolean>(false);

  const schema = z.object({
    // Only show "too short" when user has entered at least 1 digit
    phoneNumber: z
      .string()
      .trim()
      .refine((val) => val.length === 0 || val.length === 7, {
        message: "Símanúmer of stutt",
      })
      .refine((val) => val.length === 0 || /^\d+$/.test(val), {
        message: "Símanúmer má aðeins innihalda tölustafi",
      })
      .refine((val) => val.length > 0, {
        message: "Sláðu inn símanúmer",
      }),
    // Only show "too short" when user has entered at least 1 digit
    externalId: z
      .string()
      .trim()
      .refine((val) => val.length === 0 || val.length === 10, {
        message: "Kennitala of stutt",
      }),
    debtorCorpExternalId: z
      .string()
      .trim()
      .refine((val) => val.length === 0 || val.length === 10, {
        message: "Kennitala fyrirtækis of stutt",
      }),
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    defaultValues: {
      phoneNumber: localStorage.getItem("phone") || "",
      externalId: localStorage.getItem("externalId") || "",
      debtorCorpExternalId: localStorage.getItem("debtorCorpExternalId") || "",
    },
  });

  const { data: user, isError: userNotFound } = useQuery({
    queryKey: ["user", form.getValues("phoneNumber")],
    queryFn: () => getUser(`+354${form.getValues("phoneNumber")}`),
    enabled: fetchUser,
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        return false;
      }
      return true;
    },
  });

  const { data: userAccountConsent } = useQuery({
    queryKey: ["corp-account-consent", user?.id],
    queryFn: () => getAccountConsent(String(user?.id)),
    enabled: !!user?.id,
  });

  const initPaymentMutation = useMutation({
    mutationFn: async (variables: {
      phoneNumber?: string;
      externalId?: string;
      debtorCorpExternalId?: string;
    }) => {
      return initPayment(
        String(paymentId),
        variables.phoneNumber,
        variables.debtorCorpExternalId,
        variables.externalId,
      );
    },
    onSuccess: async () => {
      setRefetchInterval();
    },
    onError: (error: Response) => {
      // The handler is returning status 400 if the corporate user is not found.
      // We should fix this and make it return 404 so we can handle multiple types of error
      if (error.status === 400) {
        patchAndRedirect(); // Redirect ther user to the onboarding page.
      }
      setIsLoading(false);
      toast.error("Ekki tókst að framkvæma greiðslu");
    },
  });

  const patchPaymentMutation = useMutation({
    mutationFn: async (variables: {
      phoneNumber?: string;
      externalId?: string;
      debtorCorpExternalId?: string;
    }) => {
      // Add 5 minutes to expiresAt
      const expiration = fromUnixTime(expiresAt);
      const extendedExpiration = addMinutes(expiration, 5);
      return patchPayment(
        String(paymentId),
        variables.phoneNumber,
        variables.externalId,
        variables.debtorCorpExternalId,
        getUnixTime(extendedExpiration),
      );
    },
    onSuccess: () => {
      setRefetchInterval();
      window.location.href = window.location.href.replace("payment", "light"); // redirect to the light page
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const cancelPaymentMutation = useMutation({
    mutationFn: async () => {
      return cancelPayment(String(paymentId));
    },
    onSuccess: () => {
      setRefetchInterval();
    },
    onError: () => {
      toast.error("Ekki tókst að hætta við greiðslu");
    },
  });

  const patchAndRedirect = async () => {
    patchPaymentMutation.mutate({
      phoneNumber: `+354${form.getValues("phoneNumber")}`,
      debtorCorpExternalId: form.getValues("debtorCorpExternalId"),
      externalId: form.getValues("externalId"),
    });
    // Redirect to the onboarding page
    window.location.href = window.location.href.replace("payment", "light");
  };

  const onSubmit = () => {
    setIsLoading(true);
    localStorage.setItem("phone", form.getValues("phoneNumber"));
    localStorage.setItem(
      "debtorCorpExternalId",
      form.getValues("debtorCorpExternalId"),
    );
    localStorage.setItem("externalId", form.getValues("externalId"));
    setFetchUser(true);
  };

  useEffect(() => {
    // Redirect the user to the onboarding page if they are a shadow user.
    if (user && user.onboardedAt === 0) {
      const url = new URL(window.location.href.replace("payment", "light"));
      url.searchParams.set("id", paymentId);
      window.location.href = url.toString();
      return;
    }
    // If the user is onboarded we init the payment and polling
    if (user && user.onboardedAt > 0 && userAccountConsent) {
      // If we need the user to give us bank account consent again for isb
      if (
        !userAccountConsent.accountExists ||
        userAccountConsent.consentExpired
      ) {
        const url = new URL(window.location.href.replace("payment", "light"));
        if (userAccountConsent.consentExpired) {
          url.searchParams.append("reason", "consent");
        }

        // Redirect
        window.location.href = url.toString();
        return;
      }
      if (!userAccountConsent.consentExpired) {
        // If the consent is fine then we init the payment
        setIsLoading(true);
        initPaymentMutation.mutate({
          phoneNumber: `+354${form.getValues("phoneNumber")}`,
          debtorCorpExternalId: form.getValues("debtorCorpExternalId"),
          externalId: form.getValues("externalId"),
        });
      }
    }
  }, [user, userAccountConsent]);

  useEffect(() => {
    //isError returns true if the user is not found. Redirect to the onboading page
    if (userNotFound) {
      // Patch the payment with the phone number
      patchPaymentMutation.mutate({
        phoneNumber: `+354${form.getValues("phoneNumber")}`,
        debtorCorpExternalId: form.getValues("debtorCorpExternalId"),
        externalId: form.getValues("externalId"),
      });
    }
  }, [userNotFound]);

  useEffect(() => {
    if (form.getValues("phoneNumber")) {
      setAuthPhoneNumber(form.getValues("phoneNumber"));
    }
    if (form.getValues("externalId")) {
      setAuthExternalId(form.getValues("externalId"));
    }
  }, [form.getValues("phoneNumber"), form.getValues("externalId")]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Símanúmer</FormLabel>
              <FormControl>
                <Input
                  id="phoneNumber"
                  {...field}
                  onChange={(e) =>
                    field.onChange(e.target.value.replace(/\D/g, ""))
                  }
                  autoComplete="tel-local"
                  maxLength={7}
                  className="h-12"
                  inputMode="tel"
                  pattern="[0-9]*"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="externalId"
          render={({ field }) => (
            <FormItem className="py-2">
              <FormLabel>Kennitala</FormLabel>
              <FormControl>
                <Input
                  id="externalId"
                  {...field}
                  maxLength={10}
                  className="h-12"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="debtorCorpExternalId"
          render={({ field }) => (
            <FormItem className="pt-2 mb-4">
              <FormLabel>Kennitala fyrirtækis</FormLabel>
              <FormControl>
                <Input
                  id="debtorCorpExternalId"
                  {...field}
                  maxLength={10}
                  className="h-12"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-col gap-4">
          <Button
            type="submit"
            className={
              form.formState.disabled || isLoading
                ? `${FULL_WIDTH_TALL_BUTTON} ${GREEN_ACTION_BUTTON_DISABLED}`
                : `${FULL_WIDTH_TALL_BUTTON} ${GREEN_ACTION_BUTTON_ENABLED}`
            }
            disabled={
              !form.formState.isValid ||
              !["DRAFT", "PENDING", "SCA_REQUIRED"].includes(status)
            }
          >
            Greiða
            {isLoading && <Spinner className="size-4 absolute right-4" />}
          </Button>
          <Button
            type="button"
            variant={"outline"}
            className={`${FULL_WIDTH_TALL_BUTTON} ${OUTLINE_PRESSABLE_BUTTON}`}
            onClick={() => cancelPaymentMutation.mutate()}
            disabled={cancelPaymentMutation.isPending}
          >
            <div className="flex items-center gap-1">
              <span>Hætta við</span>
              {cancelPaymentMutation.isPending ? (
                <Spinner className="size-4" />
              ) : (
                <CircleX />
              )}
            </div>
          </Button>
          <Separator className="mt-4" />
          <p className="text-sm text-muted-foreground">
            Fyrir þá sem vilja skrá fleiri en einn bankareikning
          </p>
          <Button
            type="button"
            variant={"outline"}
            className={`${FULL_WIDTH_TALL_BUTTON} ${OUTLINE_PRESSABLE_BUTTON}`}
            onClick={openWallet}
          >
            <div className="flex items-center gap-1">
              <span>Veski</span>
              <Wallet2 />
            </div>
          </Button>
        </div>
      </form>
    </Form>
  );
}
